if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', class MediaGallery extends HTMLElement {
    constructor() {
      super();
      
      // 获取section ID和可能的ID后缀
      const sectionId = this.dataset.sectionId;
      const isDuplicate = this.id.includes('-duplicate');
      const idAppend = isDuplicate ? '-duplicate' : '';
      
      this.elements = {
        liveRegion: document.querySelector(`#GalleryStatus-${sectionId}`),
        viewer: this.querySelector(`#GalleryViewer-${sectionId}${idAppend}`),
        thumbnails: this.querySelector(`#GalleryThumbnails-${sectionId}${idAppend}`)
      }
      
      // 如果找不到viewer元素，尝试查找所有可能的slider-component
      if (!this.elements.viewer) {
        const allSliders = this.querySelectorAll('slider-component');
        
        // 如果有slider-component，使用第一个作为viewer
        if (allSliders.length > 0) {
          this.elements.viewer = allSliders[0];
        }
      }
      
      // 初始隐藏所有媒体项，防止在筛选前短暂显示
      this.initialHideAllMedia();
      
      // 设置选项变化监听器
      this.setupOptionChangeListener();
      
      // 确保初始筛选被应用
      this.ensureInitialFilter();
    }
    
    // 初始隐藏所有媒体项，防止在筛选前短暂显示
    initialHideAllMedia() {
      // 由于已在HTML中直接添加initial-loading类，这里不需要再添加
      // 这个方法保留以确保兼容性，但不执行任何操作
      if (!this.elements.viewer) {
        return;
      }
    }
    
    // 确保初始筛选被正确应用
    ensureInitialFilter() {
      // 添加标志防止重复筛选
      if (this.initialFilterApplied) {
        return;
      }
      
      // 使用setTimeout确保DOM完全加载后再执行筛选
      setTimeout(() => {
        if (!this.dataset.optionsId) {
          // 如果没有optionsId，只需移除初始加载类
          this.removeInitialLoadingClasses();
          this.initialFilterApplied = true;
          return;
        }
        
        const optionsElement = document.getElementById(this.dataset.optionsId);
        if (!optionsElement) {
          // 如果找不到options元素，只需移除初始加载类
          this.removeInitialLoadingClasses();
          this.initialFilterApplied = true;
          return;
        }
        
        const initialOptions = optionsElement.getAttribute('data-options');
        
        if (initialOptions) {
          this.filterMediaByOptions(initialOptions.split(','));
        } else {
          // 如果没有初始选项，只需移除初始加载类
          this.removeInitialLoadingClasses();
        }
        
        // 标记初始筛选已应用
        this.initialFilterApplied = true;
      }, 5); // 进一步减少延迟时间，更快响应
    }
    
    // 移除初始加载类
    removeInitialLoadingClasses() {
      if (!this.elements.viewer) {
        return;
      }
      
      const allMediaItems = this.elements.viewer.querySelectorAll('[data-media-id]');
      const allThumbnails = this.elements.thumbnails ? this.elements.thumbnails.querySelectorAll('[data-target]') : [];
      
      allMediaItems.forEach(item => {
        item.classList.remove('initial-loading');
        
        // 移除scroll-trigger--offscreen类，确保媒体项可见
        if (item.classList.contains('scroll-trigger')) {
          item.classList.remove('scroll-trigger--offscreen');
        }
      });
      
      allThumbnails.forEach(thumb => {
        thumb.classList.remove('initial-loading');
        
        // 移除scroll-trigger--offscreen类，确保缩略图可见
        if (thumb.classList.contains('scroll-trigger')) {
          thumb.classList.remove('scroll-trigger--offscreen');
        }
      });
    }
    
    // 设置属性值变化监听器
    setupOptionChangeListener() {
      if (!this.dataset.optionsId) {
        return;
      }
      
      const optionsElement = document.getElementById(this.dataset.optionsId);
      if (!optionsElement) {
        return;
      }
      
      // 创建MutationObserver监听属性值变化
      this.observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes' && mutation.attributeName === 'data-options') {
            const selectedOptions = optionsElement.getAttribute('data-options').split(',');
            this.filterMediaByOptions(selectedOptions);
          }
        });
      });
      
      // 开始观察
      this.observer.observe(optionsElement, { attributes: true });
      
      // 移除这里的初始筛选调用，因为ensureInitialFilter方法已经处理了
      // const initialOptions = optionsElement.getAttribute('data-options');
      // if (initialOptions) {
      //   this.filterMediaByOptions(initialOptions.split(','));
      // }
    }
    
    // 根据属性值筛选媒体
    filterMediaByOptions(selectedOptions) {
      if (!selectedOptions || selectedOptions.length === 0) {
        return;
      }
      
      // 检查viewer元素是否存在
      if (!this.elements.viewer) {
        return;
      }
      
      // 将选项数组转换为字符串以便比较
      const optionsString = selectedOptions.join(',');
      
      // 检查是否已经应用了相同的筛选选项
      if (this.dataset.currentOptions === optionsString) {
        // 选项未变化，不需要重新筛选，直接返回
        return;
      }
      
      // 记录当前应用的筛选选项
      this.dataset.currentOptions = optionsString;
      
      // 获取所有媒体项
      const allMediaItems = this.elements.viewer.querySelectorAll('[data-media-id]');
      const allThumbnails = this.elements.thumbnails ? this.elements.thumbnails.querySelectorAll('[data-target]') : [];
      
      // 如果没有媒体项，直接返回
      if (allMediaItems.length === 0) {
        return;
      }
      
      // 移除初始加载类
      this.removeInitialLoadingClasses();
      
      // 如果没有属性值选择，显示所有媒体
      if (selectedOptions.length === 1 && selectedOptions[0] === '') {
        // 重置所有媒体项的可见性
        allMediaItems.forEach(item => {
          item.classList.remove('filtered-out');
          item.style.display = '';
        });
        
        allThumbnails.forEach(thumb => {
          thumb.classList.remove('filtered-out');
          thumb.style.display = '';
        });
        return;
      }
      
      // 执行筛选逻辑
      this.performFiltering(selectedOptions, allMediaItems, allThumbnails);
      
      // 重置滑动组件，确保显示第一个可见媒体
      // 跳过设置活动媒体，因为我们将在product-info.js中显式设置
      this.resetToFirstVisibleMedia(true);
    }
    
    // 执行媒体筛选逻辑
    performFiltering(selectedOptions, allMediaItems, allThumbnails) {
      // 如果没有属性值选择，显示所有媒体
      if (selectedOptions.length === 1 && selectedOptions[0] === '') {
        return;
      }
      
      // 预处理选中的选项，转换为小写并过滤空值
      const processedOptions = selectedOptions
        .filter(option => option && option.trim() !== '')
        .map(option => option.toLowerCase());
      
      // 如果没有有效的选项，不执行筛选
      if (processedOptions.length === 0) {
        return;
      }
      
      // 筛选逻辑：根据data-media-options属性匹配
      allMediaItems.forEach((item) => {
        const mediaId = item.dataset.mediaId;
        const mediaContainer = item.querySelector('.product-media-container');
        const mediaOptions = mediaContainer ? mediaContainer.dataset.mediaOptions || '' : '';
        
        // 检查是否匹配当前选中的属性值
        let shouldShow = false;
        
        // 特殊处理：如果媒体项的Alt text为"NN"，则总是显示
        const mediaAlt = mediaContainer?.dataset.alt || '';
        if (mediaAlt.toLowerCase() === 'nn') {
          shouldShow = true;
        } 
        // 特殊处理：如果媒体项的Alt text以"MM"结尾，检查是否匹配任何选中的选项
        else if (mediaAlt.toLowerCase().endsWith('mm')) {
          // 检查Alt text是否包含逗号，表示多个金属属性
          if (mediaAlt.includes(',')) {
            // 处理多个金属属性的情况，如"14KMM,18KMM"
            const metalOptions = mediaAlt.toLowerCase().split(',').map(opt => opt.trim());
            // 检查是否有任何选中的选项与任何金属属性的基础部分匹配
            shouldShow = metalOptions.some(metalOption => {
              if (metalOption.endsWith('mm')) {
                const baseOption = metalOption.slice(0, -2);
                return processedOptions.some(option => option === baseOption);
              }
              return false;
            });
          } else {
            // 处理单个金属属性的情况，如"14KMM"
            const baseAlt = mediaAlt.toLowerCase().slice(0, -2);
            // 检查是否有任何选中的选项与基础部分精确匹配
            shouldShow = processedOptions.some(option => option === baseAlt);
          }
        } 
        // 常规处理：检查媒体选项是否包含所有选中的属性值
        else if (mediaOptions) {
          // 将媒体选项字符串按逗号分割成数组
          const mediaOptionsArray = mediaOptions.toLowerCase().split(',').map(opt => opt.trim());
          
          // 过滤掉图片顺序标识（如O1, O2, O3等）
          const filteredMediaOptions = mediaOptionsArray.filter(option => 
            !/^[oO]\d+$/.test(option) // 排除以O或o开头后跟数字的选项
          );
          
          // 检查是否包含所有选中的属性值
          shouldShow = processedOptions.every(option => {
            // 检查是否有任何媒体选项与选中的选项精确匹配
            return filteredMediaOptions.includes(option);
          });
        }
        
        // 根据筛选结果设置媒体项的可见性
        if (shouldShow) {
          item.classList.remove('filtered-out');
          item.style.display = '';
          
          // 移除scroll-trigger--offscreen类，确保媒体项可见
          if (item.classList.contains('scroll-trigger')) {
            item.classList.remove('scroll-trigger--offscreen');
          }
        } else {
          item.classList.add('filtered-out');
          item.style.display = 'none';
        }
        
        // 同步处理缩略图（如果存在）
        if (this.elements.thumbnails) {
          const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
          if (thumbnail) {
            if (shouldShow) {
              thumbnail.classList.remove('filtered-out');
              thumbnail.style.display = '';
              
              // 移除scroll-trigger--offscreen类，确保缩略图可见
              if (thumbnail.classList.contains('scroll-trigger')) {
                thumbnail.classList.remove('scroll-trigger--offscreen');
              }
            } else {
              thumbnail.classList.add('filtered-out');
              thumbnail.style.display = 'none';
            }
          }
        }
      });
    }
    
    // 重置到第一个可见的媒体项
    // @param {boolean} skipSetActiveMedia - 是否跳过设置活动媒体
    resetToFirstVisibleMedia(skipSetActiveMedia = false) {
      // 获取第一个可见的媒体项
      const firstVisibleMedia = this.elements.viewer.querySelector('.media-gallery__slide:not(.filtered-out)');
      
      // 如果有可见的媒体项，设置为活动媒体
      if (firstVisibleMedia) {
        const mediaId = firstVisibleMedia.dataset.mediaId;
        
        // 确保第一个可见媒体项移除了scroll-trigger--offscreen类
        if (firstVisibleMedia.classList.contains('scroll-trigger')) {
          firstVisibleMedia.classList.remove('scroll-trigger--offscreen');
        }
        
        // 如果不跳过设置活动媒体，则设置
        if (!skipSetActiveMedia) {
          // 设置为活动媒体
          this.setActiveMedia(mediaId);
        }
        
        // 更新滑动组件到正确位置
        const slider = this.elements.viewer.querySelector('slider-component');
        if (slider) {
          const slides = Array.from(slider.querySelectorAll('.media-gallery__slide'));
          const visibleSlides = slides.filter(slide => !slide.classList.contains('filtered-out'));
          const activeIndex = visibleSlides.findIndex(slide => slide.dataset.mediaId === mediaId);
          
          if (activeIndex > 0 && slider.scrollTo) {
            // 计算滚动位置
            const slideWidth = firstVisibleMedia.offsetWidth;
            const scrollPosition = activeIndex * slideWidth;
            slider.scrollTo({
              left: scrollPosition,
              behavior: 'smooth'
            });
          }
        }
        
        // 更新滑动按钮状态
        this.updateSliderButtons();
      }
      // 移除没有可见媒体项时的处理逻辑，避免重复调用setActiveMedia
    }
    
    // 更新滑动按钮状态
    updateSliderButtons() {
      const slider = this.elements.viewer.querySelector('slider-component');
      if (!slider) return;
      
      const prevButton = slider.querySelector('.slider-button--prev');
      const nextButton = slider.querySelector('.slider-button--next');
      
      if (!prevButton || !nextButton) return;
      
      // 获取所有可见的幻灯片
      const visibleSlides = Array.from(slider.querySelectorAll('.media-gallery__slide:not(.filtered-out)'));
      
      // 如果只有一个或没有可见幻灯片，禁用所有按钮
      if (visibleSlides.length <= 1) {
        prevButton.disabled = true;
        nextButton.disabled = true;
        return;
      }
      
      // 获取当前活动的幻灯片
      const activeSlide = slider.querySelector('.media-gallery__slide.is-active');
      if (!activeSlide) return;
      
      // 找到当前活动幻灯片在可见幻灯片中的索引
      const activeIndex = visibleSlides.findIndex(slide => slide.dataset.mediaId === activeSlide.dataset.mediaId);
      
      // 更新按钮状态
      prevButton.disabled = activeIndex <= 0;
      nextButton.disabled = activeIndex >= visibleSlides.length - 1;
    }

    onSlideChanged(event) {
      if (event.detail.currentElement) {
        if (this.elements.thumbnails) {
          const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${ event.detail.currentElement.dataset.mediaId }"]`);
          this.setActiveThumbnail(thumbnail);
        }
        // 更新滑动按钮状态
        this.updateSliderButtons();
      }
    }

    setActiveMedia(mediaId, prepend) {
      // 移除所有媒体项的激活状态
      this.querySelectorAll('li[data-media-id]').forEach((mediaItem) => {
        mediaItem.classList.remove('is-active');
      });
      
      // 查找目标媒体项
      const activeMedia = this.querySelector(`li[data-media-id="${mediaId}"]`);
      
      if (!activeMedia) {
        return;
      }
      
      // 检查目标媒体项是否被过滤掉
      const isFiltered = activeMedia.classList.contains('filtered');
      let mediaToActivate = activeMedia;
      
      if (isFiltered) {
        // 如果目标媒体项被过滤掉，查找第一个可见的媒体项
        const firstVisibleMedia = this.querySelector('li[data-media-id]:not(.filtered)');
        if (firstVisibleMedia) {
          mediaToActivate = firstVisibleMedia;
        } else {
          // 如果没有可见的媒体项，使用原始媒体项但移除过滤状态
          activeMedia.classList.remove('filtered');
          mediaToActivate = activeMedia;
        }
      }
      
      // 确保要激活的媒体项移除了scroll-trigger--offscreen类
      if (mediaToActivate.classList.contains('scroll-trigger')) {
        mediaToActivate.classList.remove('scroll-trigger--offscreen');
      }
      
      // 设置激活状态
      mediaToActivate.classList.add('is-active');
      
      // 如果需要前置，将激活的媒体项移动到第一位
      if (prepend && mediaToActivate !== activeMedia) {
        mediaToActivate.parentElement.prepend(mediaToActivate);
      } else if (prepend) {
        activeMedia.parentElement.prepend(activeMedia);
      }
    }

    setActiveThumbnail(thumbnail) {
      if (!this.elements.thumbnails || !thumbnail) return;

      this.elements.thumbnails.querySelectorAll('button').forEach((element) => element.removeAttribute('aria-current'));
      thumbnail.querySelector('button').setAttribute('aria-current', true);
      if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

      this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
    }

    announceLiveRegion(activeItem, position) {
      const image = activeItem.querySelector('.product__modal-opener--image img');
      if (!image) return;
      image.onload = () => {
        this.elements.liveRegion.setAttribute('aria-hidden', false);
        this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace(
          '[index]',
          position
        );
        setTimeout(() => {
          this.elements.liveRegion.setAttribute('aria-hidden', true);
        }, 2000);
      };
      image.src = image.src;
    }

    playActiveMedia(activeItem) {
      window.pauseAllMedia();
      const deferredMedia = activeItem.querySelector('.deferred-media');
      if (deferredMedia) deferredMedia.loadContent(false);
    }

    preventStickyHeader() {
      this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
      if (!this.stickyHeader) return;
      this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
    }

    removeListSemantic() {
      if (!this.elements.viewer.slider) return;
      this.elements.viewer.slider.setAttribute('role', 'presentation');
      this.elements.viewer.sliderItems.forEach(slide => slide.setAttribute('role', 'presentation'));
    }
  });
}
